import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TradingAccountOpen.css';
import { API_URL } from '../config/data';
import { logout, checkAuth, handleAuthError } from '../utils/auth';

const TradingAccountOpen = () => {
  const [currency, setCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const currencies = [
    { value: 'USD', label: 'USD - Доллар США' },
    { value: 'EUR', label: 'EUR - Евро' },
    { value: 'RUB', label: 'RUB - Российский рубль' },
    { value: 'CNY', label: 'CNY - Китайский юань' }
  ];

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Загрузка профиля пользователя при монтировании компонента
  useEffect(() => {
    if (!checkAuth()) {
      return;
    }

    const loadUserProfile = async () => {
      try {
        const userData = await fetchUserProfile();
        setUser(userData);
      } catch (error) {
        console.error('Ошибка при загрузке профиля:', error);
        handleAuthError(error, navigate);
      }
    };

    loadUserProfile();
  }, [navigate]);

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.post(`${API_URL}/api/trading-accounts`, 
        { currency },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Перенаправляем на страницу счетов
        navigate('/account/expense', { 
          state: { 
            message: `Торговый счет в валюте ${currency} успешно открыт!`,
            accountNumber: response.data.account.account_number
          }
        });
      } else {
        setError(response.data.message || 'Произошла ошибка при создании торгового счета');
      }
    } catch (error) {
      console.error('Ошибка:', error);
      if (error.response?.status === 401) {
        handleAuthError(error, navigate);
      } else {
        setError(error.response?.data?.message || 'Произошла ошибка при создании торгового счета');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="#" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">Открытие торгового счёта</h3>
              
              <div data-fls-block="" className="trading-account-open__form-wrapper">
                <div className="trading-account-open__field">
                  <label className="trading-account-open__label">
                    Выберите валюту торгового счета
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="trading-account-open__select input"
                    required
                  >
                    {currencies.map((curr) => (
                      <option key={curr.value} value={curr.value}>
                        {curr.label}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <div className="trading-account-open__error">
                    {error}
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                className="trading-account-open__submit button"
                disabled={isLoading}
              >
                {isLoading ? 'Открываем счет...' : 'Открыть торговый счет'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TradingAccountOpen;